import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    LogIn, LogOut, Check, X, Clock, Mail, Phone, User,
    Plus, Edit2, Trash2, FileText, Users as UsersIcon, Calendar as CalendarIcon,
    MessageSquare, Save, ArrowLeft, ExternalLink,
    Bold, Italic, Heading2, Heading3, List, Quote, Link2, Eye, Code2,
    Sparkles, Settings as SettingsIcon, KeyRound,
} from "lucide-react";
import {
    adminLogin,
    listAppointmentsAdmin,
    updateAppointmentStatus,
    listContactsAdmin,
    listReferrersAdmin,
    listBlogPosts,
    adminCreateBlogPost,
    adminUpdateBlogPost,
    adminDeleteBlogPost,
    adminDeleteContact,
    getSiteSettings,
    adminUpdateSiteSettings,
    adminChangeCredentials,
    formatApiError,
} from "@/lib/api";
import { DOMAIN_LABEL, SLOT_LABEL, LOCATION_LABEL } from "@/lib/content";
import { renderMarkdown } from "@/lib/markdown";
import { useRef } from "react";

const STATUS_META = {
    pending: { label: "En attente", color: "#d89b92" },
    confirmed: { label: "Confirmé", color: "#6b5548" },
    rejected: { label: "Refusé", color: "#a63333" },
    done: { label: "Fait", color: "#5a6560" },
};

const TABS = [
    { key: "appointments", label: "Rendez-vous", icon: CalendarIcon },
    { key: "messages", label: "Messages", icon: MessageSquare },
    { key: "blog", label: "Journal", icon: FileText },
    { key: "referrers", label: "Médecins référents", icon: UsersIcon },
    { key: "site", label: "Réglages du site", icon: SettingsIcon },
    { key: "account", label: "Mon compte", icon: KeyRound },
];

export default function Admin() {
    const [authed, setAuthed] = useState(!!localStorage.getItem("ana_admin_token"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("appointments");

    const login = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const r = await adminLogin(email, password);
            localStorage.setItem("ana_admin_token", r.token);
            setAuthed(true);
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    const logout = () => {
        localStorage.removeItem("ana_admin_token");
        setAuthed(false);
    };

    if (!authed) {
        return (
            <main data-testid="admin-login" style={{ paddingTop: 160, minHeight: "100vh" }}>
                <section style={{ padding: "40px 40px 120px" }}>
                    <div className="mx-auto" style={{ maxWidth: 520 }}>
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Espace praticien</div>
                        <div className="divider-terra mt-3" />
                        <h1 className="font-display mt-8" style={{ fontSize: "3rem", lineHeight: 1, fontWeight: 400, color: "var(--forest-dark)" }}>
                            Connexion.
                        </h1>
                        <p style={{ fontSize: 14, color: "var(--ink-mid)", marginTop: 12 }}>
                            Accès réservé à Ana pour la gestion des demandes, messages, articles et médecins référents.
                        </p>

                        <form onSubmit={login} data-testid="admin-login-form" style={{ marginTop: 40, background: "var(--cream)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}>
                            <div className="mb-6">
                                <label className="field-label">Email</label>
                                <input required type="email" data-testid="admin-email" className="field-clinical" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@anacorpsetsouffle.ch" />
                            </div>
                            <div>
                                <label className="field-label">Mot de passe</label>
                                <input required type="password" data-testid="admin-password" className="field-clinical" value={password} onChange={(e) => setPassword(e.target.value)} />
                            </div>
                            {error && <div data-testid="admin-login-error" style={{ marginTop: 16, color: "#a63333", fontSize: 13 }}>{error}</div>}
                            <button type="submit" data-testid="admin-submit" className="btn-primary mt-8">
                                <LogIn size={14} /> Se connecter
                            </button>
                        </form>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main data-testid="admin-dashboard" style={{ paddingTop: 160, minHeight: "100vh" }}>
            <section style={{ padding: "40px 40px 40px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Espace praticien</div>
                            <div className="divider-terra mt-3" />
                            <h1 className="font-display mt-6" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", lineHeight: 1, fontWeight: 400, color: "var(--forest-dark)" }}>
                                Tableau de <span style={{ fontStyle: "italic", color: "var(--terra)" }}>bord</span>.
                            </h1>
                        </div>
                        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="mt-12 flex items-center gap-6 flex-wrap" style={{ borderBottom: "1px solid var(--hairline)" }}>
                        {TABS.map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                onClick={() => setTab(key)}
                                data-testid={`admin-tab-${key}`}
                                style={{
                                    background: "transparent", border: "none",
                                    padding: "12px 0",
                                    fontFamily: "var(--font-body)",
                                    fontSize: 13, letterSpacing: "0.05em",
                                    color: tab === key ? "var(--forest-dark)" : "var(--ink-soft)",
                                    fontWeight: tab === key ? 600 : 400,
                                    borderBottom: `2px solid ${tab === key ? "var(--terra)" : "transparent"}`,
                                    marginBottom: -1,
                                    cursor: "pointer",
                                    display: "inline-flex", alignItems: "center", gap: 8,
                                }}
                            >
                                <Icon size={14} /> {label}
                            </button>
                        ))}
                    </div>

                    {tab === "appointments" && <AppointmentsPanel />}
                    {tab === "messages" && <MessagesPanel />}
                    {tab === "blog" && <BlogPanel />}
                    {tab === "referrers" && <ReferrersPanel />}
                    {tab === "site" && <SitePanel />}
                    {tab === "account" && <AccountPanel currentEmail={email} onEmailUpdated={setEmail} />}
                </div>
            </section>
        </main>
    );
}

/* ================================================================== */
/*  APPOINTMENTS                                                       */
/* ================================================================== */
function AppointmentsPanel() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = () => {
        setLoading(true);
        listAppointmentsAdmin()
            .then(setItems)
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(refresh, []);

    const setStatus = async (id, status) => {
        await updateAppointmentStatus(id, status);
        refresh();
    };

    const visible = filter === "all" ? items : items.filter((i) => i.status === filter);

    return (
        <div style={{ marginTop: 32 }}>
            <div className="flex items-center gap-3 flex-wrap mb-8">
                {["all", "pending", "confirmed", "rejected", "done"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        data-testid={`filter-${k}`}
                        style={{
                            background: filter === k ? "var(--forest-dark)" : "transparent",
                            color: filter === k ? "var(--cream)" : "var(--ink-mid)",
                            border: `1px solid ${filter === k ? "var(--forest-dark)" : "var(--hairline)"}`,
                            padding: "6px 14px", borderRadius: 999,
                            fontSize: 12, letterSpacing: "0.05em", cursor: "pointer",
                        }}
                    >
                        {k === "all" ? "Tous" : STATUS_META[k].label} ({k === "all" ? items.length : items.filter((i) => i.status === k).length})
                    </button>
                ))}
            </div>

            {loading && <div style={{ color: "var(--ink-soft)" }}>Chargement…</div>}
            {error && <div style={{ color: "#a63333" }}>{error}</div>}

            <div className="grid grid-cols-1 gap-4">
                {visible.map((a) => (
                    <motion.div
                        key={a.id}
                        data-testid={`appt-row-${a.id}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 20, borderRadius: 4 }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                    >
                        <div className="md:col-span-4">
                            <div className="flex items-center gap-2">
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_META[a.status]?.color || "#888" }} />
                                <span className="font-mono-clinical" style={{ color: STATUS_META[a.status]?.color }}>
                                    {STATUS_META[a.status]?.label}
                                </span>
                            </div>
                            <div className="font-display mt-2" style={{ fontSize: 20, fontWeight: 500, color: "var(--forest-dark)" }}>
                                {a.patient_name}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>
                                Demande : {new Date(a.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                        </div>
                        <div className="md:col-span-3" style={{ fontSize: 13 }}>
                            <div className="flex items-center gap-2 mb-2"><Phone size={12} style={{ color: "var(--terra)" }} /> <a href={`tel:${a.patient_phone}`} className="link-underline">{a.patient_phone}</a></div>
                            <div className="flex items-center gap-2"><Mail size={12} style={{ color: "var(--terra)" }} /> <a href={`mailto:${a.patient_email}`} className="link-underline">{a.patient_email}</a></div>
                            {a.referring_doctor && <div className="flex items-center gap-2 mt-2"><User size={12} style={{ color: "var(--terra)" }} /> {a.referring_doctor}</div>}
                        </div>
                        <div className="md:col-span-3" style={{ fontSize: 13 }}>
                            <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>Souhait</div>
                            <div className="mt-2">
                                <Clock size={12} style={{ color: "var(--terra)", display: "inline", marginRight: 6 }} />
                                {new Date(a.preferred_date).toLocaleDateString("fr-CH", { weekday: "short", day: "numeric", month: "short" })} · {SLOT_LABEL[a.preferred_slot] || a.preferred_slot}
                            </div>
                            <div className="mt-2" style={{ fontSize: 12, color: "var(--forest)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                                {DOMAIN_LABEL[a.domain] || a.domain}
                                <span style={{ margin: "0 6px", color: "var(--hairline)" }}>·</span>
                                <span style={{ color: a.location === "domicile" ? "var(--terra)" : "var(--forest)" }}>
                                    {LOCATION_LABEL[a.location] || "En cabinet"}
                                </span>
                            </div>
                            {a.message && <div style={{ marginTop: 8, fontSize: 12, color: "var(--ink-mid)", fontStyle: "italic" }}>&laquo; {a.message} &raquo;</div>}
                        </div>
                        <div className="md:col-span-2 flex flex-col gap-2 items-end">
                            {a.status === "pending" && (
                                <>
                                    <button data-testid={`accept-${a.id}`} onClick={() => setStatus(a.id, "confirmed")} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }}>
                                        <Check size={12} /> Confirmer
                                    </button>
                                    <button data-testid={`reject-${a.id}`} onClick={() => setStatus(a.id, "rejected")} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                                        <X size={12} /> Refuser
                                    </button>
                                </>
                            )}
                            {a.status === "confirmed" && (
                                <button data-testid={`done-${a.id}`} onClick={() => setStatus(a.id, "done")} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                                    Marquer fait
                                </button>
                            )}
                            {(a.status === "rejected" || a.status === "done") && (
                                <button data-testid={`reset-${a.id}`} onClick={() => setStatus(a.id, "pending")} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                                    Rouvrir
                                </button>
                            )}
                        </div>
                    </motion.div>
                ))}
                {!loading && visible.length === 0 && (
                    <div style={{ textAlign: "center", padding: 60, color: "var(--ink-soft)", fontSize: 14 }}>
                        Aucune demande dans cette catégorie.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ================================================================== */
/*  MESSAGES                                                           */
/* ================================================================== */
function MessagesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = () => {
        setLoading(true);
        listContactsAdmin().then(setItems).finally(() => setLoading(false));
    };
    useEffect(refresh, []);

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement ce message ?")) return;
        try {
            await adminDeleteContact(id);
            refresh();
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    if (loading) return <div style={{ marginTop: 32, color: "var(--ink-soft)" }}>Chargement…</div>;

    return (
        <div style={{ marginTop: 32 }} className="grid grid-cols-1 gap-4">
            {error && <div style={{ color: "#a63333" }}>{error}</div>}
            {items.map((m) => (
                <div key={m.id} data-testid={`message-${m.id}`} style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 24, borderRadius: 4 }}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="font-display" style={{ fontSize: 20, fontWeight: 500, color: "var(--forest-dark)" }}>{m.name}</div>
                            <div style={{ fontSize: 13, color: "var(--ink-mid)", marginTop: 4 }}>
                                <a href={`mailto:${m.email}`} className="link-underline">{m.email}</a>{m.phone && <> · <a href={`tel:${m.phone}`} className="link-underline">{m.phone}</a></>}
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: "0.14em" }}>
                                {new Date(m.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                            <button
                                onClick={() => remove(m.id)}
                                data-testid={`message-delete-${m.id}`}
                                title="Supprimer (spam)"
                                className="btn-ghost"
                                style={{ padding: "6px 10px", fontSize: 11, color: "#a63333", borderColor: "rgba(166,51,51,0.3)" }}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                    {m.subject && (
                        <div className="mt-4 font-display" style={{ fontSize: 16, color: "var(--forest-dark)", fontStyle: "italic" }}>&laquo; {m.subject} &raquo;</div>
                    )}
                    <div className="mt-4" style={{ fontSize: 14, lineHeight: 1.65, color: "var(--ink-mid)", whiteSpace: "pre-wrap" }}>
                        {m.message}
                    </div>
                </div>
            ))}
            {items.length === 0 && (
                <div style={{ textAlign: "center", padding: 60, color: "var(--ink-soft)", fontSize: 14 }}>
                    Aucun message reçu pour l'instant.
                </div>
            )}
        </div>
    );
}

/* ================================================================== */
/*  BLOG EDITOR                                                        */
/* ================================================================== */
const BLOG_CATEGORIES = ["Cardio-respiratoire", "Périnée", "Général", "Cabinet"];

function slugify(str) {
    return str
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 90);
}

function BlogPanel() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // null | "new" | slug
    const [error, setError] = useState(null);

    const refresh = () => {
        setLoading(true);
        listBlogPosts().then(setPosts).finally(() => setLoading(false));
    };
    useEffect(refresh, []);

    const startNew = () => setEditing("new");
    const startEdit = (slug) => setEditing(slug);
    const cancel = () => setEditing(null);

    const remove = async (slug) => {
        if (!window.confirm("Supprimer définitivement cet article ?")) return;
        try {
            await adminDeleteBlogPost(slug);
            refresh();
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    if (editing) {
        const post = editing === "new" ? null : posts.find((p) => p.slug === editing);
        return (
            <BlogEditor
                post={post}
                onCancel={cancel}
                onSaved={() => { setEditing(null); refresh(); }}
            />
        );
    }

    return (
        <div style={{ marginTop: 32 }}>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                <div style={{ fontSize: 14, color: "var(--ink-mid)" }}>
                    {posts.length} article{posts.length > 1 ? "s" : ""} publié{posts.length > 1 ? "s" : ""}.
                </div>
                <button onClick={startNew} data-testid="blog-new" className="btn-primary" style={{ padding: "10px 20px", fontSize: 13 }}>
                    <Plus size={14} /> Nouvel article
                </button>
            </div>

            {error && <div style={{ color: "#a63333", marginBottom: 16 }}>{error}</div>}
            {loading && <div style={{ color: "var(--ink-soft)" }}>Chargement…</div>}

            <div className="grid grid-cols-1 gap-4">
                {posts.map((p) => (
                    <div
                        key={p.slug}
                        data-testid={`blog-row-${p.slug}`}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                        style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 16, borderRadius: 4 }}
                    >
                        <div className="md:col-span-2" style={{ aspectRatio: "4/3", overflow: "hidden", borderRadius: 3 }}>
                            <img src={p.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <div className="md:col-span-7">
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>{p.category} · {p.reading_time} min</div>
                            <div className="font-display mt-2" style={{ fontSize: 18, color: "var(--forest-dark)", fontWeight: 500, lineHeight: 1.2 }}>
                                {p.title}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>
                                Publié le {new Date(p.published_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" })}
                                {" · /journal/"}<span style={{ fontFamily: "monospace" }}>{p.slug}</span>
                            </div>
                        </div>
                        <div className="md:col-span-3 flex gap-2 justify-end flex-wrap">
                            <a href={`/journal/${p.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                                <ExternalLink size={12} /> Voir
                            </a>
                            <button onClick={() => startEdit(p.slug)} data-testid={`blog-edit-${p.slug}`} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                                <Edit2 size={12} /> Éditer
                            </button>
                            <button onClick={() => remove(p.slug)} data-testid={`blog-delete-${p.slug}`} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12, color: "#a63333", borderColor: "rgba(166, 51, 51, 0.3)" }}>
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                ))}
                {!loading && posts.length === 0 && (
                    <div style={{ textAlign: "center", padding: 60, color: "var(--ink-soft)", fontSize: 14 }}>
                        Aucun article. Cliquez sur « Nouvel article » pour commencer.
                    </div>
                )}
            </div>
        </div>
    );
}

function BlogEditor({ post, onCancel, onSaved }) {
    const isNew = !post;
    const bodyRef = useRef(null);
    const [preview, setPreview] = useState(false);
    const [f, setF] = useState({
        slug: post?.slug || "",
        title: post?.title || "",
        excerpt: post?.excerpt || "",
        body: post?.body || "",
        category: post?.category || BLOG_CATEGORIES[0],
        reading_time: post?.reading_time || 5,
        cover_image: post?.cover_image || "",
        published_at: post?.published_at || new Date().toISOString().slice(0, 10),
    });
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [slugTouched, setSlugTouched] = useState(!isNew);

    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

    // Auto-slug from title (only in "new" mode until user edits slug manually)
    useEffect(() => {
        if (!isNew || slugTouched) return;
        set("slug", slugify(f.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [f.title]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = { ...f, reading_time: parseInt(f.reading_time, 10) };
            if (isNew) {
                await adminCreateBlogPost(payload);
            } else {
                const { slug, ...rest } = payload;
                await adminUpdateBlogPost(post.slug, rest);
            }
            onSaved();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="blog-editor" style={{ marginTop: 32 }}>
            <div className="flex items-center gap-4 mb-8">
                <button type="button" onClick={onCancel} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>
                    <ArrowLeft size={12} /> Retour
                </button>
                <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                    {isNew ? "Nouvel article" : "Édition"}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-8">
                    <div style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}>
                        <div className="mb-6">
                            <label className="field-label">Titre</label>
                            <input required data-testid="blog-title" className="field-clinical" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex: Post-COVID, quelle rééducation respiratoire ?" style={{ fontSize: 20, fontFamily: "var(--font-display)" }} />
                        </div>

                        <div className="mb-6">
                            <label className="field-label">Chapô / excerpt</label>
                            <textarea required minLength={10} rows={2} data-testid="blog-excerpt" className="field-clinical" value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="Résumé qui apparaît sur la liste du journal…" style={{ resize: "vertical", fontFamily: "var(--font-body)" }} />
                        </div>

                        <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
                            <label className="field-label" style={{ margin: 0 }}>Corps de l'article</label>
                            <div style={{ display: "flex", gap: 4 }}>
                                <button type="button" data-testid="blog-preview-toggle"
                                    onClick={() => setPreview(!preview)}
                                    style={{
                                        display: "inline-flex", alignItems: "center", gap: 6,
                                        padding: "6px 12px",
                                        background: preview ? "var(--forest-dark)" : "transparent",
                                        color: preview ? "var(--cream)" : "var(--forest-dark)",
                                        border: "1px solid var(--forest-dark)",
                                        borderRadius: 999,
                                        fontSize: 11, letterSpacing: "0.06em",
                                        cursor: "pointer",
                                    }}
                                >
                                    <Eye size={12} /> {preview ? "Édition" : "Aperçu"}
                                </button>
                            </div>
                        </div>

                        <MarkdownToolbar textareaRef={bodyRef} onChange={(v) => set("body", v)} />

                        {preview ? (
                            <div
                                data-testid="blog-body-preview"
                                className="prose-clinical"
                                style={{
                                    minHeight: 300,
                                    padding: "24px 24px",
                                    background: "var(--cream-warm)",
                                    border: "1px solid var(--hairline)",
                                    borderRadius: 3,
                                }}
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(f.body || "*Rien à prévisualiser.*") }}
                            />
                        ) : (
                            <textarea
                                required minLength={10}
                                data-testid="blog-body"
                                ref={bodyRef}
                                rows={20}
                                className="field-clinical"
                                value={f.body}
                                onChange={(e) => set("body", e.target.value)}
                                placeholder={"## Un premier titre\n\nUn paragraphe.\n\n**Un mot en gras**, un *mot en italique*.\n\n- élément\n- autre élément\n\n> Une citation clinique."}
                                style={{ resize: "vertical", fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontSize: 14, lineHeight: 1.7 }}
                            />
                        )}
                        <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 6 }}>
                            Markdown supporté : <code>**gras**</code>, <code>*italique*</code>, <code>## Titre</code>, <code>### Sous-titre</code>, <code>- liste</code>, <code>&gt; citation</code>, <code>[lien](url)</code>.
                        </div>
                    </div>
                </div>

                <div className="md:col-span-4">
                    <div style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 24, borderRadius: 4 }}>
                        <div className="mb-6">
                            <label className="field-label">Slug (URL)</label>
                            <input required data-testid="blog-slug" className="field-clinical" value={f.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} placeholder="url-de-l-article" disabled={!isNew} />
                            <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 6 }}>
                                /journal/{f.slug || "…"}
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="field-label">Catégorie</label>
                            <select required data-testid="blog-category" className="field-clinical" value={f.category} onChange={(e) => set("category", e.target.value)}>
                                {BLOG_CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
                            </select>
                        </div>
                        <div className="mb-6">
                            <label className="field-label">Temps de lecture (min)</label>
                            <input required type="number" min={1} max={60} data-testid="blog-reading" className="field-clinical" value={f.reading_time} onChange={(e) => set("reading_time", e.target.value)} />
                        </div>
                        <div className="mb-6">
                            <label className="field-label">Date de publication</label>
                            <input required type="date" data-testid="blog-date" className="field-clinical" value={f.published_at} onChange={(e) => set("published_at", e.target.value)} />
                        </div>
                        <div className="mb-6">
                            <label className="field-label">Image de couverture (URL)</label>
                            <input required data-testid="blog-cover" className="field-clinical" value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://images.unsplash.com/…" />
                            {f.cover_image && (
                                <div style={{ marginTop: 12, aspectRatio: "16/9", overflow: "hidden", borderRadius: 3 }}>
                                    <img src={f.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                </div>
                            )}
                        </div>

                        {error && <div style={{ color: "#a63333", fontSize: 13, marginBottom: 12 }}>{error}</div>}

                        <button type="submit" disabled={saving} data-testid="blog-save" className="btn-primary" style={{ opacity: saving ? 0.5 : 1, width: "100%", justifyContent: "center" }}>
                            <Save size={14} /> {saving ? "Enregistrement…" : isNew ? "Publier" : "Mettre à jour"}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}

/* ================================================================== */
/*  REFERRERS DIRECTORY                                                */
/* ================================================================== */
function ReferrersPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        listReferrersAdmin().then(setItems).finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ marginTop: 32, color: "var(--ink-soft)" }}>Chargement…</div>;

    const total = items.reduce((s, r) => s + (r.referrals_count || 0), 0);

    return (
        <div style={{ marginTop: 32 }}>
            <div className="mb-8" style={{ fontSize: 14, color: "var(--ink-mid)" }}>
                <strong>{items.length}</strong> médecin{items.length > 1 ? "s" : ""} référencé{items.length > 1 ? "s" : ""},
                {" "}<strong>{total}</strong> patient{total > 1 ? "s" : ""} adressé{total > 1 ? "s" : ""} au total.
            </div>

            <div className="grid grid-cols-1 gap-3">
                {items.map((r) => (
                    <div
                        key={r.id}
                        data-testid={`referrer-row-${r.id}`}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                        style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 20, borderRadius: 4 }}
                    >
                        <div className="md:col-span-4">
                            <div className="font-display" style={{ fontSize: 18, color: "var(--forest-dark)", fontWeight: 500 }}>
                                {r.name}
                            </div>
                            {r.cabinet && <div style={{ fontSize: 13, color: "var(--ink-mid)", fontStyle: "italic", marginTop: 2 }}>{r.cabinet}</div>}
                        </div>
                        <div className="md:col-span-4">
                            {r.email ? (
                                <a href={`mailto:${r.email}`} className="link-underline" style={{ fontSize: 13, color: "var(--forest-dark)" }}>
                                    <Mail size={12} style={{ display: "inline", marginRight: 6, color: "var(--terra)" }} />{r.email}
                                </a>
                            ) : (
                                <span style={{ fontSize: 12, color: "var(--ink-soft)", fontStyle: "italic" }}>— pas d'email —</span>
                            )}
                        </div>
                        <div className="md:col-span-2 text-center">
                            <div style={{ fontSize: 11, color: "var(--terra)", textTransform: "uppercase", letterSpacing: "0.14em" }}>Adressés</div>
                            <div className="font-display" style={{ fontSize: 26, color: "var(--forest-dark)", fontStyle: "italic", lineHeight: 1 }}>
                                {r.referrals_count}
                            </div>
                        </div>
                        <div className="md:col-span-2 text-right" style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: "0.12em" }}>
                            Depuis {new Date(r.created_at).toLocaleDateString("fr-CH", { month: "short", year: "numeric" })}
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div style={{ textAlign: "center", padding: 60, color: "var(--ink-soft)", fontSize: 14 }}>
                        Aucun médecin référent enregistré pour l'instant.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ================================================================== */
/*  SITE SETTINGS — banner de la home                                  */
/* ================================================================== */
function SitePanel() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        getSiteSettings()
            .then(setSettings)
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const set = (k, v) => {
        setSaved(false);
        setSettings((s) => ({ ...s, [k]: v }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const updated = await adminUpdateSiteSettings({
                opening_banner_enabled: !!settings.opening_banner_enabled,
                opening_banner_text: settings.opening_banner_text || "",
            });
            setSettings(updated);
            setSaved(true);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ marginTop: 32, color: "var(--ink-soft)" }}>Chargement…</div>;
    if (!settings) return <div style={{ marginTop: 32, color: "#a63333" }}>{error}</div>;

    return (
        <div style={{ marginTop: 32 }} className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <form onSubmit={submit} data-testid="site-settings-form" className="md:col-span-8" style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}>
                <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                    <Sparkles size={12} style={{ display: "inline", marginRight: 6 }} />
                    Bandeau d'ouverture
                </div>
                <div className="divider-terra mt-3" />
                <h2 className="font-display mt-6" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", lineHeight: 1.1, color: "var(--forest-dark)", fontWeight: 400 }}>
                    Annoncez l'<span style={{ fontStyle: "italic", color: "var(--terra)" }}>ouverture</span> du cabinet.
                </h2>
                <p style={{ fontSize: 14, color: "var(--ink-mid)", marginTop: 12, lineHeight: 1.6, maxWidth: 480 }}>
                    Ce bandeau apparaît en haut de toutes les pages du site. Vous pouvez le
                    désactiver d'un clic une fois le cabinet en fonctionnement.
                </p>

                <div className="mt-8 flex items-center justify-between" style={{ padding: "16px 20px", background: "var(--cream-warm)", border: "1px solid var(--hairline)", borderRadius: 4 }}>
                    <div>
                        <div style={{ fontSize: 14, color: "var(--forest-dark)", fontWeight: 500 }}>
                            Afficher le bandeau
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 2 }}>
                            {settings.opening_banner_enabled ? "Visible sur toutes les pages" : "Masqué"}
                        </div>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={settings.opening_banner_enabled}
                        data-testid="site-banner-toggle"
                        onClick={() => set("opening_banner_enabled", !settings.opening_banner_enabled)}
                        style={{
                            position: "relative",
                            width: 52, height: 28,
                            borderRadius: 999,
                            background: settings.opening_banner_enabled ? "var(--terra)" : "var(--hairline)",
                            border: "none", cursor: "pointer",
                            transition: "background 0.25s ease",
                        }}
                    >
                        <span
                            style={{
                                position: "absolute", top: 3, left: settings.opening_banner_enabled ? 27 : 3,
                                width: 22, height: 22, borderRadius: "50%",
                                background: "#fff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                                transition: "left 0.25s ease",
                            }}
                        />
                    </button>
                </div>

                <div className="mt-6">
                    <label className="field-label">Texte du bandeau</label>
                    <input
                        data-testid="site-banner-text"
                        className="field-clinical"
                        value={settings.opening_banner_text || ""}
                        onChange={(e) => set("opening_banner_text", e.target.value)}
                        placeholder="Ex : Ouverture début décembre 2026"
                        maxLength={200}
                    />
                    <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 6 }}>
                        Court et clair. {200 - (settings.opening_banner_text || "").length} caractères restants.
                    </div>
                </div>

                {error && <div style={{ marginTop: 20, color: "#a63333", fontSize: 13 }}>{error}</div>}
                {saved && <div style={{ marginTop: 20, color: "var(--forest)", fontSize: 13 }}>Modifications enregistrées.</div>}

                <button type="submit" disabled={saving} data-testid="site-settings-save" className="btn-primary mt-8" style={{ opacity: saving ? 0.5 : 1 }}>
                    <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
            </form>

            <div className="md:col-span-4">
                <div style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 24, borderRadius: 4 }}>
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Aperçu</div>
                    <div className="divider-terra mt-3" />
                    <div className="mt-6" style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                        Voici comment le bandeau apparaît sur le site :
                    </div>
                    <div
                        data-testid="site-banner-preview"
                        style={{
                            marginTop: 16,
                            background: settings.opening_banner_enabled
                                ? "linear-gradient(90deg, var(--terra-light) 0%, var(--terra) 60%, var(--terra-deep) 100%)"
                                : "var(--hairline)",
                            color: settings.opening_banner_enabled ? "#fff" : "var(--ink-soft)",
                            textAlign: "center",
                            padding: "10px 12px",
                            borderRadius: 4,
                            fontSize: 12, letterSpacing: "0.08em",
                            textTransform: "uppercase", fontWeight: 500,
                            fontStyle: settings.opening_banner_enabled ? "normal" : "italic",
                        }}
                    >
                        {settings.opening_banner_enabled
                            ? (settings.opening_banner_text || "Aucun texte")
                            : "Bandeau masqué"}
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ================================================================== */
/*  ACCOUNT PANEL — change admin email & password                      */
/* ================================================================== */
function AccountPanel({ currentEmail, onEmailUpdated }) {
    const [me, setMe] = useState(currentEmail || "");
    const [newEmail, setNewEmail] = useState("");
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [newPwConfirm, setNewPwConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch authoritative email in case user reloaded and email state is empty
    useEffect(() => {
        if (!me) {
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            fetch(`${base}/api/admin/me`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("ana_admin_token")}` },
            })
                .then((r) => r.json())
                .then((d) => {
                    if (d?.email) {
                        setMe(d.email);
                        onEmailUpdated?.(d.email);
                    }
                })
                .catch(() => { });
        }
    }, [me, onEmailUpdated]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!newEmail && !newPw) {
            setError("Renseignez un nouvel email ou un nouveau mot de passe.");
            return;
        }
        if (newPw && newPw.length < 8) {
            setError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
            return;
        }
        if (newPw && newPw !== newPwConfirm) {
            setError("La confirmation du mot de passe ne correspond pas.");
            return;
        }

        setSaving(true);
        try {
            const payload = { current_password: currentPw };
            if (newEmail) payload.new_email = newEmail;
            if (newPw) payload.new_password = newPw;
            const r = await adminChangeCredentials(payload);
            // Persist the new session token so subsequent requests keep working
            localStorage.setItem("ana_admin_token", r.token);
            setMe(r.email);
            onEmailUpdated?.(r.email);
            setSuccess(
                (newEmail && newPw) ? "Email et mot de passe modifiés."
                    : newEmail ? "Email modifié."
                        : "Mot de passe modifié.",
            );
            setNewEmail("");
            setCurrentPw("");
            setNewPw("");
            setNewPwConfirm("");
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ marginTop: 32 }} className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <form onSubmit={submit} data-testid="account-form" className="md:col-span-8" style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}>
                <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                    <KeyRound size={12} style={{ display: "inline", marginRight: 6 }} />
                    Sécurité du compte
                </div>
                <div className="divider-terra mt-3" />
                <h2 className="font-display mt-6" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", lineHeight: 1.1, color: "var(--forest-dark)", fontWeight: 400 }}>
                    Modifier mon <span style={{ fontStyle: "italic", color: "var(--terra)" }}>identifiant</span>.
                </h2>
                <p style={{ fontSize: 14, color: "var(--ink-mid)", marginTop: 12, lineHeight: 1.6, maxWidth: 520 }}>
                    Changez librement l'email et/ou le mot de passe utilisés
                    pour vous connecter à votre espace. Votre mot de passe
                    actuel est demandé pour valider la modification.
                </p>

                {/* Current email display */}
                <div className="mt-8" style={{ padding: "14px 18px", background: "var(--cream-warm)", border: "1px solid var(--hairline)", borderRadius: 4, fontSize: 13, color: "var(--ink-mid)" }}>
                    Email actuel : <strong style={{ color: "var(--forest-dark)" }} data-testid="account-current-email">{me || "…"}</strong>
                </div>

                {/* Current password (required) */}
                <div className="mt-6">
                    <label className="field-label">Mot de passe actuel <span style={{ color: "var(--terra)" }}>*</span></label>
                    <input
                        required
                        type="password"
                        data-testid="account-current-password"
                        className="field-clinical"
                        value={currentPw}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        autoComplete="current-password"
                    />
                </div>

                {/* Change email */}
                <div className="mt-6">
                    <label className="field-label">Nouvel email <span style={{ color: "var(--ink-soft)", fontSize: 11 }}>(laisser vide pour ne pas changer)</span></label>
                    <input
                        type="email"
                        data-testid="account-new-email"
                        className="field-clinical"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="contact@anacorpsetsouffle.ch"
                    />
                </div>

                {/* Change password */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="field-label">Nouveau mot de passe <span style={{ color: "var(--ink-soft)", fontSize: 11 }}>(min. 8 caractères)</span></label>
                        <input
                            type="password"
                            data-testid="account-new-password"
                            className="field-clinical"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>
                    <div>
                        <label className="field-label">Confirmer le mot de passe</label>
                        <input
                            type="password"
                            data-testid="account-new-password-confirm"
                            className="field-clinical"
                            value={newPwConfirm}
                            onChange={(e) => setNewPwConfirm(e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                {error && <div data-testid="account-error" style={{ marginTop: 20, color: "#a63333", fontSize: 13 }}>{error}</div>}
                {success && <div data-testid="account-success" style={{ marginTop: 20, color: "var(--forest)", fontSize: 13 }}>{success}</div>}

                <button
                    type="submit"
                    disabled={saving}
                    data-testid="account-submit"
                    className="btn-primary mt-8"
                    style={{ opacity: saving ? 0.5 : 1 }}
                >
                    <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer les modifications"}
                </button>
            </form>

            <div className="md:col-span-4">
                <div style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 24, borderRadius: 4 }}>
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Bon à savoir</div>
                    <div className="divider-terra mt-3" />
                    <ul className="mt-5" style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.7, listStyle: "none", padding: 0 }}>
                        <li style={{ marginBottom: 12 }}>
                            <strong style={{ color: "var(--forest-dark)" }}>Une seule administratrice.</strong> Ce site n'a qu'un compte praticien — le vôtre.
                        </li>
                        <li style={{ marginBottom: 12 }}>
                            <strong style={{ color: "var(--forest-dark)" }}>Version en ligne & preview.</strong> Chaque version du site a sa propre base — pensez à mettre à jour vos identifiants sur les deux si nécessaire.
                        </li>
                        <li>
                            <strong style={{ color: "var(--forest-dark)" }}>En cas d'oubli</strong> du mot de passe, contactez-moi (l'agent Emergent) pour une réinitialisation.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}


/* ================================================================== */
/*  MARKDOWN TOOLBAR — used inside BlogEditor                          */
/* ================================================================== */
function MarkdownToolbar({ textareaRef, onChange }) {
    const wrap = (before, after = "", block = false) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const value = ta.value;
        const selected = value.slice(start, end) || (block ? "Texte" : "");
        const prefix = value.slice(0, start);
        const suffix = value.slice(end);
        // For block inserts (H2, H3, list, quote) — ensure they start on a new line
        let insert = `${before}${selected}${after}`;
        if (block && start > 0 && prefix[prefix.length - 1] !== "\n") {
            insert = `\n\n${insert}`;
        }
        const next = `${prefix}${insert}${suffix}`;
        onChange(next);
        // restore selection after react state update
        setTimeout(() => {
            ta.focus();
            const cursor = prefix.length + insert.length - after.length;
            ta.setSelectionRange(cursor, cursor);
        }, 0);
    };

    const btnStyle = {
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32,
        background: "transparent",
        border: "1px solid var(--hairline)",
        borderRadius: 3,
        color: "var(--forest-dark)",
        cursor: "pointer",
    };

    const buttons = [
        { icon: Bold, title: "Gras (**texte**)", do: () => wrap("**", "**") },
        { icon: Italic, title: "Italique (*texte*)", do: () => wrap("*", "*") },
        { icon: Heading2, title: "Titre (## Titre)", do: () => wrap("## ", "", true) },
        { icon: Heading3, title: "Sous-titre (### Sous-titre)", do: () => wrap("### ", "", true) },
        { icon: List, title: "Liste (- item)", do: () => wrap("- ", "", true) },
        { icon: Quote, title: "Citation (> texte)", do: () => wrap("> ", "", true) },
        {
            icon: Link2, title: "Lien ([texte](url))",
            do: () => {
                const url = window.prompt("URL du lien :", "https://");
                if (url) wrap("[", `](${url})`);
            },
        },
        { icon: Code2, title: "Code (`code`)", do: () => wrap("`", "`") },
    ];

    return (
        <div
            data-testid="markdown-toolbar"
            style={{
                display: "flex", gap: 4, flexWrap: "wrap",
                padding: 8,
                background: "var(--cream-warm)",
                border: "1px solid var(--hairline)",
                borderBottom: "none",
                borderRadius: "3px 3px 0 0",
            }}
        >
            {buttons.map(({ icon: Icon, title, do: fn }, i) => (
                <button
                    key={i}
                    type="button"
                    title={title}
                    data-testid={`md-btn-${i}`}
                    onClick={fn}
                    style={btnStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--forest-dark)"; e.currentTarget.style.color = "var(--cream)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--forest-dark)"; }}
                >
                    <Icon size={14} />
                </button>
            ))}
        </div>
    );
}

